import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useFetch } from "../../services/hooks/useFetch";
import { userApiPrefix } from "../../utils/main";
import Marketing from "./Marketing";
import { useFusionAuth, type UserInfo } from "@fusionauth/react-sdk";
import { type Group } from "@fusionauth/typescript-client";

export default function CompanySelection() {
  const navigate = useNavigate();
  const { refreshToken, fetchUserInfo, userInfo } = useFusionAuth<UserInfo>();
  const [selectedGroup, setSelectedGroup] = useState<string | undefined>(
    userInfo?.companyId,
  );
  const {
    data: groups,
    fetch: getUserGroups,
    isLoading: groupsLoading,
    status: groupStatus,
  } = useFetch<Group[]>();
  const { update: updateSelectedGroup } = useFetch();

  function handleGroupSelection() {
    void updateSelectedGroup({
      path: `${userApiPrefix}/user/group/${selectedGroup}`,
      postResponseAction: async () => {
        await refreshToken();
        await fetchUserInfo();
        navigate("/");
      },
    });
  }

  useEffect(() => {
    if (!groupsLoading && groupStatus === null) {
      void getUserGroups(`${userApiPrefix}/user/groups`);
    }
  }, [groupsLoading, groupStatus, getUserGroups]);

  return (
    <div className="signup-layout">
      <Marketing />
      <div className="authentication">
        <div className="login-form">
          <div>
            <Typography variant="h6" gutterBottom>
              You have Logged In!
            </Typography>
            <Typography variant="body2" gutterBottom>
              Before you continue, please select which company you'd like to
              use.
            </Typography>
          </div>
          {groupsLoading || !groups ? (
            <Skeleton animation="wave" />
          ) : (
            <div className="terms-of-service-box" style={{ width: "inherit" }}>
              <FormControl fullWidth>
                <InputLabel id="company-select">Company</InputLabel>
                <Select
                  label="Company"
                  labelId="company-select"
                  name="company"
                  value={selectedGroup || ""}
                  variant="outlined"
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedGroup(value);
                  }}
                  SelectDisplayProps={{
                    style: {
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    },
                  }}
                >
                  {groups!.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          )}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            data-testid="company-select-submit"
            disabled={!groups || !selectedGroup}
            onClick={handleGroupSelection}
          >
            Select Company
          </Button>
          {userInfo?.companyId && (
            <Button
              variant="contained"
              color="primary"
              type="submit"
              data-testid="back-to-home"
              onClick={() => navigate("/")}
            >
              Back To Home
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
